import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Divider,
  Alert,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Backdrop,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Close,
  Send,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Header from '../components/Header';
import { supabase } from '../lib/supabaseClient';
import { login } from '../lib/auth';
import { trackLogin } from '../lib/analytics';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        navigate('/');
      }
    };
    
    checkAuth();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData);
      
      if (result.success && result.user) {
        console.log('Login successful:', result.user);
        // Track successful login
        trackLogin('email');
        // Dispatch login success event
        window.dispatchEvent(new CustomEvent('loginSuccess'));
        // Navigate to home page after successful login
        navigate('/');
      } else {
        setError(result.error || t('auth.login.loginError'));
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t('auth.login.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) return;
    
    setForgotPasswordLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        console.error('Password reset error:', error);
        setError('Грешка при заявка за смяна на парола');
        return;
      }
      
      setForgotPasswordSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Възникна грешка при заявка за смяна на парола');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordEmail('');
    setForgotPasswordSuccess(false);
  };

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <Header />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
            <Container maxWidth="sm" sx={{ width: '80%' }}>
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'rgba(26, 26, 46, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              {t('auth.login.title') || 'Добре дошли в Workio'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#a0a0a0' }}>
              {t('auth.login.subtitle') || 'Влезте в акаунта си, за да продължите'}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              name="email"
              type="email"
              label={t('auth.login.email') || 'Имейл адрес'}
              value={formData.email}
              onChange={handleChange}
              required
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#ffffff' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              label={t('auth.login.password') || 'Парола'}
              value={formData.password}
              onChange={handleChange}
              required
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#ffffff' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#ffffff' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #be185d 100%)',
                },
              }}
            >
              {loading ? (t('auth.login.signingIn') || 'Влизане...') : (t('auth.login.signIn') || 'Влез')}
            </Button>
          </Box>

          {/* Divider */}
          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="body2" sx={{ color: '#a0a0a0', px: 2 }}>
              {t('auth.login.or') || 'или'}
            </Typography>
          </Divider>

          {/* Social Login Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              sx={{
                borderColor: '#2d2d44',
                color: '#ffffff',
                py: 1.5,
                '&:hover': {
                  borderColor: '#28A0F0',
                  backgroundColor: 'rgba(40, 160, 240, 0.1)',
                },
              }}
            >
              {t('auth.login.google') || 'Google'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              sx={{
                borderColor: '#2d2d44',
                color: '#ffffff',
                py: 1.5,
                '&:hover': {
                  borderColor: '#28A0F0',
                  backgroundColor: 'rgba(40, 160, 240, 0.1)',
                },
              }}
            >
              {t('auth.login.linkedin') || 'LinkedIn'}
            </Button>
          </Box>

          {/* Links */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 1 }}>
              {t('auth.login.noAccount') || 'Нямате акаунт?'}{' '}
              <Link
                component={RouterLink}
                to="/register"
                sx={{
                  color: '#28A0F0',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('auth.login.signUp') || 'Регистрирайте се'}
              </Link>
            </Typography>
            <Link
              component="button"
              onClick={() => setForgotPasswordOpen(true)}
              sx={{
                color: '#a0a0a0',
                textDecoration: 'none',
                fontSize: '0.875rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: '#28A0F0',
                  textDecoration: 'underline',
                },
              }}
            >
              {t('auth.login.forgotPassword') || 'Забравена парола?'}
            </Link>
          </Box>
        </Paper>
      </Container>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleCloseForgotPassword}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700,
            fontSize: '1.5rem',
            textAlign: 'center',
            py: 3,
            position: 'relative',
          }}
        >
          {forgotPasswordSuccess ? (t('auth.login.emailSent') || 'Имейл изпратен!') : (t('auth.login.forgotPasswordTitle') || 'Забравена парола')}
          <IconButton
            onClick={handleCloseForgotPassword}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#a0a0a0',
              '&:hover': {
                color: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {forgotPasswordSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <Send sx={{ fontSize: 40, color: '#ffffff' }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 2, fontWeight: 600 }}>
                {t('auth.login.checkEmail') || 'Проверете имейла си'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#a0a0a0', lineHeight: 1.6 }}>
                {t('auth.login.emailSentMessage') || 'Изпратихме ви линк за възстановяване на паролата на адрес'}{' '}
                <Box component="span" sx={{ color: '#28A0F0', fontWeight: 600 }}>
                  {forgotPasswordEmail}
                </Box>
                . {t('auth.login.checkInbox') || 'Моля, проверете пощенската си кутия и следвайте инструкциите.'}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3, textAlign: 'center' }}>
                {t('auth.login.forgotPasswordSubtitle') || 'Въведете имейл адреса си и ще ви изпратим линк за възстановяване на паролата.'}
              </Typography>
              
              <TextField
                fullWidth
                type="email"
                label={t('auth.login.email') || 'Имейл адрес'}
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: '#ffffff' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </DialogContent>

        {!forgotPasswordSuccess && (
          <DialogActions sx={{ p: 4, pt: 0 }}>
            <Button
              onClick={handleCloseForgotPassword}
              sx={{
                color: '#a0a0a0',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              {t('common.cancel') || 'Отказ'}
            </Button>
            <Button
              onClick={handleForgotPassword}
              disabled={!forgotPasswordEmail || forgotPasswordLoading}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                px: 4,
                py: 1,
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #be185d 100%)',
                },
                '&:disabled': {
                  background: '#2d2d44',
                  color: '#6b7280',
                },
              }}
            >
              {forgotPasswordLoading ? (t('auth.login.sending') || 'Изпращане...') : (t('auth.login.send') || 'Изпрати')}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      </Box>
    </Box>
  );
};

export default LoginPage;
